/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { setLocation } from 'js/lib/coursera.redirect';

import { Typography2, breakpoints } from '@coursera/cds-core';

import CourseActionBox from 'bundles/course-cards/components/course-card/enterprise/CourseActionBox';
import type { PropsForWithModal as PropsForCourseAndPartnerNameWithModal } from 'bundles/course-cards/components/course-card/enterprise/CourseAndPartnerName';
import CourseAndPartnerName, {
  CourseAndPartnerNameWithModal,
} from 'bundles/course-cards/components/course-card/enterprise/CourseAndPartnerName';
import CourseCardAutoenrollmentDisclaimer from 'bundles/course-cards/components/course-card/enterprise/CourseCardAutoenrollmentDisclaimer';
import CourseHomeLink from 'bundles/course-cards/components/course-card/enterprise/CourseHomeLink';
import SkillPathwayTakeAssessmentButton from 'bundles/course-cards/components/course-card/enterprise/SkillPathwayTakeAssessmentButton';
import TogglableContent from 'bundles/course-cards/components/course-card/enterprise/TogglableContent';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useEnterpriseBadgeMetadata } from 'bundles/unified-home-common/contexts/EnterpriseBadgeMetadataContext';

import _t from 'i18n!nls/course-cards';

const styles = {
  skillPathwayActionBox: css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    ${breakpoints.down('xs')} {
      align-items: center;
    }
  `,
};

type Props = {
  course: CoursesV1;
  cardDropdownWidget: JSX.Element;
  isAutoenrolled?: boolean;
  isPartOfS12n?: boolean;
} & Omit<PropsForCourseAndPartnerNameWithModal, 'courseId' | 'courseName' | 'partnerIds'>;

const CourseEnrolledDefaultCard: React.FC<Props> = ({
  course: { id, name, partnerIds, homeLink, description, isInactive },
  cardDropdownWidget,
  isAutoenrolled,
  isPartOfS12n,
  ...rest
}) => {
  const badgeMetadata = useEnterpriseBadgeMetadata();
  const program = useProgram();
  const isSkillPathway = badgeMetadata?.isSkillPathway;

  return (
    <div className="rc-CourseEnrolledDefaultCard row flex-1">
      <div className="col-xs-12 col-sm-8 col-md-9 pos-static-override-hack">
        {isSkillPathway ? (
          <CourseAndPartnerName
            courseId={id}
            courseName={name}
            partnerIds={partnerIds}
            isPartOfS12n={isPartOfS12n}
            onClick={() =>
              program?.metadata?.slug &&
              badgeMetadata?.badge &&
              setLocation(`/programs/${program?.metadata?.slug}/learning-path/${badgeMetadata.badge.slug}`)
            }
            {...rest}
          />
        ) : (
          <CourseAndPartnerNameWithModal
            courseId={id}
            courseName={name}
            partnerIds={partnerIds}
            isPartOfS12n={isPartOfS12n}
            {...rest}
          />
        )}
        {cardDropdownWidget}
        {isPartOfS12n && (
          <Typography2 component="p" className="m-b-1" variant="bodyPrimary">
            {_t('Started')}
          </Typography2>
        )}
        {description && (
          <TogglableContent>
            <Typography2 component="p" variant="bodySecondary">
              {description}
            </Typography2>
          </TogglableContent>
        )}
        {isAutoenrolled && <CourseCardAutoenrollmentDisclaimer />}
      </div>
      <CourseActionBox>
        {isSkillPathway ? (
          <div css={styles.skillPathwayActionBox}>
            {!isInactive && (
              <CourseHomeLink homeLink={homeLink} label={_t('Resume')} trackingName="resume_button" courseName={name} />
            )}
            <SkillPathwayTakeAssessmentButton courseId={id} />
          </div>
        ) : (
          <div>
            {!isInactive && (
              <CourseHomeLink homeLink={homeLink} label={_t('Resume')} trackingName="resume_button" courseName={name} />
            )}
          </div>
        )}
      </CourseActionBox>
    </div>
  );
};

export default CourseEnrolledDefaultCard;
